import React, { useMemo } from "react";
import { TrendingUp, TrendingDown, Minus } from "lucide-react";
import {
    BarChart, Bar, ResponsiveContainer, XAxis, YAxis, CartesianGrid, Tooltip, Legend, Cell,
} from "recharts";
import { Panel } from "@/components/common/Panel";

const SEV_COLORS = {
    critical: "var(--neon-red)", high: "#FF5C5C", medium: "var(--warning)", low: "var(--metal)", info: "var(--neon-cyan)",
};

/** Shared KPI-delta + severity-bar + category-diff rendering used by both
 * cross-workspace Compare and single-workspace Timeline Diff — both backend
 * endpoints return the identical { kpi_delta, category_diff, a.severity, b.severity } shape. */
export function DiffResultView({ result, labelA = "A", labelB = "B" }) {
    const severityData = useMemo(() => {
        if (!result) return [];
        return ["critical", "high", "medium", "low", "info"].map((s) => ({
            name: s.toUpperCase(),
            A: result.a.severity[s] || 0,
            B: result.b.severity[s] || 0,
            fill: SEV_COLORS[s],
        }));
    }, [result]);

    if (!result) return null;
    return (
        <>
            <Panel eyebrow="KPI Delta" title={`${labelA} → ${labelB}`}>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                    {Object.entries(result.kpi_delta).map(([k, v]) => {
                        const delta = v.delta;
                        const Up = delta > 0 ? TrendingUp : delta < 0 ? TrendingDown : Minus;
                        const isRiskKpi = k === "risk_score";
                        const negativeDir = isRiskKpi ? delta > 0 : delta < 0;
                        const tone = delta === 0 ? "text-slate-300" : (negativeDir ? "text-rose-400" : "text-emerald-400");
                        return (
                            <div key={k} className="panel px-3 py-3">
                                <div className="label-eyebrow">{k.replace("_", " ")}</div>
                                <div className="flex items-end justify-between mt-2">
                                    <div>
                                        <div className="font-mono text-[11px] text-slate-500">{labelA}: {v.a}</div>
                                        <div className="font-mono text-[11px] text-slate-300">{labelB}: {v.b}</div>
                                    </div>
                                    <div className={`flex items-center gap-1 font-display text-lg ${tone}`}>
                                        <Up size={14} />
                                        {delta > 0 ? `+${delta}` : delta}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </Panel>

            <Panel eyebrow="Severity Distribution" title={`Findings · ${labelA} vs ${labelB}`}>
                <ResponsiveContainer minWidth={1} minHeight={1} width="100%" height={260}>
                    <BarChart data={severityData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,0.07)" />
                        <XAxis dataKey="name" stroke="#94a3b8" tick={{ fontSize: 10, fontFamily: "IBM Plex Mono" }} />
                        <YAxis stroke="#94a3b8" allowDecimals={false} tick={{ fontSize: 10 }} />
                        <Tooltip contentStyle={{ background: "var(--bg-elevated)", border: "1px solid rgb(var(--accent-1-rgb) / 0.25)" }} />
                        <Legend wrapperStyle={{ fontSize: 10, fontFamily: "IBM Plex Mono", textTransform: "uppercase" }} />
                        <Bar dataKey="A" name={labelA} fill="#94A3B8" radius={[2, 2, 0, 0]} />
                        <Bar dataKey="B" name={labelB} radius={[2, 2, 0, 0]}>
                            {severityData.map((d) => <Cell key={d.name} fill={d.fill} />)}
                        </Bar>
                    </BarChart>
                </ResponsiveContainer>
            </Panel>

            <Panel eyebrow="Category Diff" title={`Detection categories · ${labelA} vs ${labelB}`}>
                <table className="tbl">
                    <thead><tr><th>Category</th><th className="text-right">{labelA}</th><th className="text-right">{labelB}</th><th className="text-right">Δ</th></tr></thead>
                    <tbody>
                        {result.category_diff.length === 0 && <tr><td colSpan={4} className="text-center text-xs text-slate-500 py-4">No categories detected.</td></tr>}
                        {result.category_diff.map((row) => {
                            const tone = row.delta === 0 ? "text-slate-300" : (row.delta > 0 ? "text-rose-400" : "text-emerald-400");
                            return (
                                <tr key={row.category}>
                                    <td>{row.category}</td>
                                    <td className="text-right font-mono">{row.a}</td>
                                    <td className="text-right font-mono">{row.b}</td>
                                    <td className={`text-right font-display ${tone}`}>{row.delta > 0 ? `+${row.delta}` : row.delta}</td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </Panel>
        </>
    );
}
